import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  useContatosAgente,
  useSalvarContatoAgente,
  useAudienciasAgente,
  useSalvarAudienciaGrupo,
  type ContatoAgente,
  type ContatoInput,
  type AudienciaAgente,
  type AudienciaGrupoInput,
} from "@/hooks/use-agent";
import { useFuncionarios } from "@/hooks/use-equipe";
import { Loader2, Plus, Pencil, Users } from "lucide-react";

// Aba Contatos de /agente — quem pode receber o boletim (Ciclo 2).
//
// Sem seed automático: depois do deploy, o Hugo se cadastra aqui e liga a
// assinatura na aba Envio automático. Um seed com o email dele chumbado no
// código funcionaria uma vez e mentiria para toda org nova.
//
// Mobile-first: lista de cartões, um formulário por vez, nada de tabela.

const PAPEIS: Array<{ valor: ContatoInput["papel"]; rotulo: string }> = [
  { valor: "gestor", rotulo: "Gestor — recebe tudo" },
  { valor: "operador", rotulo: "Operador — recebe o que é dele" },
  { valor: "observador", rotulo: "Observador — só leitura" },
];

const VAZIO: ContatoInput = { nome: "", email: "", telefone: "", papel: "gestor", ativo: true };

/** Radix Select não aceita `value=""`; o "sem vínculo" precisa de um valor próprio. */
const SEM_FUNCIONARIO = "__nenhum__";

function paraFormulario(c: ContatoAgente): ContatoInput {
  return {
    nome: c.nome,
    email: c.email ?? "",
    telefone: c.telefone ?? "",
    papel: c.papel,
    funcionarioId: c.funcionarioId,
    ativo: c.ativo,
  };
}

export function ContatosAgente() {
  const { toast } = useToast();
  const contatos = useContatosAgente();
  const salvar = useSalvarContatoAgente();
  // Vínculo com funcionário é OPCIONAL e some quando o módulo Equipe está
  // desabilitado (a rota recusa) — o cadastro de contato não pode depender dele.
  const funcionarios = useFuncionarios();
  const podeVincular = !funcionarios.isError && (funcionarios.data ?? []).length > 0;

  const [editando, setEditando] = useState<{ id?: string; dados: ContatoInput } | null>(null);

  const handleSalvar = () => {
    if (!editando) return;
    const dados: ContatoInput = {
      ...editando.dados,
      nome: editando.dados.nome.trim(),
      email: editando.dados.email.trim(),
      // String vazia não é "sem telefone": o servidor valida E.164, e "" seria
      // recusado. Neste ciclo o telefone é opcional — vira null.
      telefone: editando.dados.telefone?.trim() ? editando.dados.telefone.trim() : null,
    };
    salvar.mutate(
      { id: editando.id, dados },
      {
        onSuccess: () => {
          toast({ title: editando.id ? "Contato atualizado" : "Contato cadastrado" });
          setEditando(null);
        },
        onError: e => toast({
          title: "Não consegui salvar",
          description: e instanceof Error ? e.message : String(e),
          variant: "destructive",
        }),
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="font-semibold">Quem recebe o boletim</h2>
          <p className="text-sm text-muted-foreground">
            Só quem estiver cadastrado e com o boletim ligado recebe.
          </p>
        </div>
        {editando === null && (
          <Button size="sm" onClick={() => setEditando({ dados: { ...VAZIO } })} data-testid="btn-novo-contato">
            <Plus className="h-4 w-4 mr-1.5" />
            Novo
          </Button>
        )}
      </div>

      {editando !== null && (
        <div className="rounded-md border p-3 space-y-3" data-testid="form-contato">
          <div className="space-y-1">
            <Label htmlFor="contato-nome" className="text-xs">Nome</Label>
            <Input
              id="contato-nome"
              value={editando.dados.nome}
              onChange={e => setEditando({ ...editando, dados: { ...editando.dados, nome: e.target.value } })}
              data-testid="input-contato-nome"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="contato-email" className="text-xs">Email</Label>
            <Input
              id="contato-email"
              type="email"
              inputMode="email"
              value={editando.dados.email}
              onChange={e => setEditando({ ...editando, dados: { ...editando.dados, email: e.target.value } })}
              data-testid="input-contato-email"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="contato-telefone" className="text-xs">Telefone (para o WhatsApp, depois)</Label>
            <Input
              id="contato-telefone"
              inputMode="tel"
              placeholder="+5574999999999"
              value={editando.dados.telefone ?? ""}
              onChange={e => setEditando({ ...editando, dados: { ...editando.dados, telefone: e.target.value } })}
              data-testid="input-contato-telefone"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Papel</Label>
            <Select
              value={editando.dados.papel}
              onValueChange={v => setEditando({ ...editando, dados: { ...editando.dados, papel: v as ContatoInput["papel"] } })}
            >
              <SelectTrigger data-testid="select-contato-papel"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAPEIS.map(p => <SelectItem key={p.valor} value={p.valor}>{p.rotulo}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {podeVincular && (
            <div className="space-y-1">
              <Label className="text-xs">Funcionário (opcional)</Label>
              <Select
                value={editando.dados.funcionarioId ?? SEM_FUNCIONARIO}
                onValueChange={v => setEditando({
                  ...editando,
                  dados: { ...editando.dados, funcionarioId: v === SEM_FUNCIONARIO ? null : v },
                })}
              >
                <SelectTrigger data-testid="select-contato-funcionario"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SEM_FUNCIONARIO}>Sem vínculo</SelectItem>
                  {(funcionarios.data ?? []).map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.apelido?.trim() || f.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center justify-between">
            <Label htmlFor="contato-ativo" className="text-xs">Ativo</Label>
            <Switch
              id="contato-ativo"
              checked={editando.dados.ativo}
              onCheckedChange={v => setEditando({ ...editando, dados: { ...editando.dados, ativo: v } })}
              data-testid="switch-contato-ativo"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              onClick={handleSalvar}
              disabled={salvar.isPending || !editando.dados.nome.trim() || !editando.dados.email.trim()}
              data-testid="btn-salvar-contato"
            >
              {salvar.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Salvar
            </Button>
            <Button variant="ghost" onClick={() => setEditando(null)} disabled={salvar.isPending}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {contatos.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
        </div>
      ) : contatos.isError ? (
        // isError nunca pode virar "nenhum contato": um 403 apareceria como lista
        // vazia e o Hugo cadastraria tudo de novo.
        <p className="text-sm text-erro py-6 text-center" data-testid="erro-contatos">
          {(contatos.error as any)?.message ?? "Não foi possível carregar os contatos."}
        </p>
      ) : (contatos.data ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">
          Nenhum contato cadastrado ainda.
        </p>
      ) : (
        <ul className="space-y-2">
          {(contatos.data ?? []).map(c => (
            <li key={c.id} className="rounded-md border p-3 flex items-start justify-between gap-2" data-testid={`contato-${c.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">{c.nome}</span>
                  {!c.ativo && <Badge variant="outline" className="text-xs">inativo</Badge>}
                </div>
                <p className="text-sm text-muted-foreground break-all">{c.email ?? "sem email"}</p>
                {c.telefone && <p className="text-xs text-muted-foreground">{c.telefone}</p>}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setEditando({ id: c.id, dados: paraFormulario(c) })}
                data-testid={`btn-editar-contato-${c.id}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-muted-foreground">
        O <strong>opt-in</strong> só vale para o WhatsApp oficial (Meta Cloud API), ainda não disponível: a pessoa
        precisa mandar uma mensagem para o número do Rizoma antes de poder receber. O WhatsApp (GZappy) e o email
        não têm essa exigência.
      </p>

      <GruposAgente />
    </div>
  );
}

// ─── Grupos (WhatsApp via GZappy — Ciclo 3a) ──────────────────────────────────
//
// Audiência de grupo é cadastro separado do contato: não é "uma pessoa", é
// nome + JID direto. Fica atrás do mesmo aviso que o envio de grupo carrega em
// todo o produto — o documento do GZappy não cobre grupo (lá o uso é 1:1), e
// não afirmamos que funciona até um envio real ter dado certo.

const GRUPO_VAZIO: AudienciaGrupoInput = { nome: "", destino: "" };

export function GruposAgente() {
  const { toast } = useToast();
  const audiencias = useAudienciasAgente();
  const salvar = useSalvarAudienciaGrupo();
  const grupos = (audiencias.data ?? []).filter(a => a.tipo === "grupo");

  const [editando, setEditando] = useState<{ id?: string; dados: AudienciaGrupoInput } | null>(null);

  const handleSalvar = () => {
    if (!editando) return;
    const dados: AudienciaGrupoInput = {
      nome: editando.dados.nome.trim(),
      destino: editando.dados.destino.trim(),
    };
    salvar.mutate(
      { id: editando.id, dados },
      {
        onSuccess: () => {
          toast({ title: editando.id ? "Grupo atualizado" : "Grupo cadastrado" });
          setEditando(null);
        },
        onError: e => toast({
          title: "Não consegui salvar",
          description: e instanceof Error ? e.message : String(e),
          variant: "destructive",
        }),
      },
    );
  };

  return (
    <div className="space-y-4 pt-2 border-t">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="font-semibold">Grupos (WhatsApp)</h2>
          <p className="text-sm text-muted-foreground">
            Envio para grupo ainda não foi validado neste número — cadastre e confira no histórico se chegou.
          </p>
        </div>
        {editando === null && (
          <Button size="sm" variant="outline" onClick={() => setEditando({ dados: { ...GRUPO_VAZIO } })} data-testid="btn-novo-grupo">
            <Plus className="h-4 w-4 mr-1.5" />
            Novo
          </Button>
        )}
      </div>

      {editando !== null && (
        <div className="rounded-md border p-3 space-y-3" data-testid="form-grupo">
          <div className="space-y-1">
            <Label htmlFor="grupo-nome" className="text-xs">Nome</Label>
            <Input
              id="grupo-nome"
              value={editando.dados.nome}
              onChange={e => setEditando({ ...editando, dados: { ...editando.dados, nome: e.target.value } })}
              data-testid="input-grupo-nome"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="grupo-jid" className="text-xs">JID do grupo</Label>
            <Input
              id="grupo-jid"
              placeholder="120363012345678901@g.us"
              value={editando.dados.destino}
              onChange={e => setEditando({ ...editando, dados: { ...editando.dados, destino: e.target.value } })}
              data-testid="input-grupo-jid"
            />
            <p className="text-xs text-muted-foreground">
              O JID aparece no painel do GZappy, na lista de grupos da instância.
            </p>
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              onClick={handleSalvar}
              disabled={salvar.isPending || !editando.dados.nome.trim() || !editando.dados.destino.trim()}
              data-testid="btn-salvar-grupo"
            >
              {salvar.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Salvar
            </Button>
            <Button variant="ghost" onClick={() => setEditando(null)} disabled={salvar.isPending}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {audiencias.isLoading ? (
        <Skeleton className="h-14" />
      ) : audiencias.isError ? (
        <p className="text-sm text-erro py-4 text-center" data-testid="erro-grupos">
          {(audiencias.error as any)?.message ?? "Não foi possível carregar os grupos."}
        </p>
      ) : grupos.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">Nenhum grupo cadastrado ainda.</p>
      ) : (
        <ul className="space-y-2">
          {grupos.map((g: AudienciaAgente) => (
            <li key={g.id} className="rounded-md border p-3 flex items-start justify-between gap-2" data-testid={`grupo-${g.id}`}>
              <div className="min-w-0 flex items-start gap-2">
                <Users className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <p className="font-medium truncate">{g.nome}</p>
                  <p className="text-xs text-muted-foreground break-all">{g.destino}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setEditando({ id: g.id, dados: { nome: g.nome, destino: g.destino } })}
                data-testid={`btn-editar-grupo-${g.id}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
